/**
 * The map. Owns the Mapbox GL instance imperatively and syncs it to React state
 * on change — GL JS keeps its own scene graph, so re-rendering markers from
 * scratch each frame would fight it and drop drag interactions.
 */

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  distanceAlongRoute,
  insertionIndexFor,
  locateWaypointsAlongRoute,
  smoothPath,
  type DirectionsResult,
  type Waypoint,
} from '@cad/core';
import { DEFAULT_CENTER, DEFAULT_ZOOM, MAP_STYLE, MAPBOX_TOKEN } from '../config';
import { loadLastView, saveLastView } from '../lib/lastView';

const ROUTE_SOURCE = 'cad-route';
const ROUTE_CASING = 'cad-route-casing';
const ROUTE_LAYER = 'cad-route-line';
/** Invisible and fat, purely so the line is grabbable without pixel-hunting. */
const ROUTE_HIT_LAYER = 'cad-route-hit';

const DRAG_SOURCE = 'cad-drag';
const DRAG_LINE = 'cad-drag-line';
const DRAG_POINT = 'cad-drag-point';

export interface MapCanvasProps {
  waypoints: Waypoint[];
  route: DirectionsResult | null;
  selectedWaypointId: string | null;
  onMapClick: (lng: number, lat: number) => void;
  onWaypointDrag: (id: string, lng: number, lat: number, commit: boolean) => void;
  onWaypointSelect: (id: string) => void;
  /** Drop a new shaping point into the middle of the route. */
  onInsertWaypoint: (index: number, lng: number, lat: number) => void;
  /** Bumped by the parent to request a fit-to-route. */
  fitRequest: number;
}

const EMPTY_COLLECTION = { type: 'FeatureCollection' as const, features: [] };

/** Pixels of travel before a press on the line counts as a drag. */
const DRAG_THRESHOLD_PX = 3;

interface DragState {
  /** Where in the waypoint array the new point will be spliced. */
  index: number;
  from: [number, number] | null;
  to: [number, number] | null;
  origin: mapboxgl.Point;
  moved: boolean;
}

export function MapCanvas(props: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef(new Map<string, mapboxgl.Marker>());
  const readyRef = useRef(false);
  /** Non-null while the user is dragging the route line. */
  const dragRef = useRef<DragState | null>(null);

  // Handlers land in a ref so the map is created exactly once while still
  // calling through to the latest closures.
  const handlers = useRef(props);
  handlers.current = props;

  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const lastView = loadLastView();
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      // Where you left it, or Norwich the first time.
      center: lastView?.center ?? DEFAULT_CENTER,
      zoom: lastView?.zoom ?? DEFAULT_ZOOM,
      attributionControl: true,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'bottom-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), 'bottom-left');
    map.addControl(new mapboxgl.GeolocateControl({ trackUserLocation: false }), 'bottom-right');

    map.on('moveend', () => {

      const centre = map.getCenter();

      saveLastView({ center: [centre.lng, centre.lat], zoom: map.getZoom() });

    });


    map.on('load', () => {
      map.addSource(ROUTE_SOURCE, { type: 'geojson', data: EMPTY_COLLECTION });
      map.addSource(DRAG_SOURCE, { type: 'geojson', data: EMPTY_COLLECTION });

      // Casing under the line so the route stays readable over dark satellite
      // imagery and busy map detail alike.
      map.addLayer({
        id: ROUTE_CASING,
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 9, 'line-opacity': 0.9 },
      });
      map.addLayer({
        id: ROUTE_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#1f6feb', 'line-width': 5 },
      });
      map.addLayer({
        id: ROUTE_HIT_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#000000', 'line-width': 22, 'line-opacity': 0 },
      });

      map.addLayer({
        id: DRAG_LINE,
        type: 'line',
        source: DRAG_SOURCE,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': '#1f6feb', 'line-width': 2.5, 'line-dasharray': [1.5, 1.5], 'line-opacity': 0.85 },
      });
      map.addLayer({
        id: DRAG_POINT,
        type: 'circle',
        source: DRAG_SOURCE,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 7,
          'circle-color': '#1f6feb',
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
        },
      });

      readyRef.current = true;
      syncRoute(map, handlers.current.route);
      wireLineDragging(map, handlers, dragRef);
    });

    map.on('click', (event) => {
      // Swallowed if the click landed on the route line, which is a drag gesture.
      if (dragRef.current) return;
      handlers.current.onMapClick(event.lngLat.lng, event.lngLat.lat);
    });

    map.getCanvas().style.cursor = 'crosshair';

    return () => {
      readyRef.current = false;
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Route line
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    syncRoute(map, props.route);
  }, [props.route]);

  // Markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const seen = new Set<string>();
    props.waypoints.forEach((waypoint, index) => {
      seen.add(waypoint.id);
      const existing = markersRef.current.get(waypoint.id);
      const label = labelFor(waypoint, index, props.waypoints.length);
      const selected = props.selectedWaypointId === waypoint.id;

      if (existing) {
        existing.setLngLat([waypoint.lng, waypoint.lat]);
        paintMarker(existing.getElement(), waypoint, label, selected);
        return;
      }

      const element = document.createElement('button');
      element.type = 'button';
      element.className = 'wp-marker';
      paintMarker(element, waypoint, label, selected);
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        handlers.current.onWaypointSelect(waypoint.id);
      });

      const marker = new mapboxgl.Marker({ element, draggable: true, anchor: 'center' })
        .setLngLat([waypoint.lng, waypoint.lat])
        .addTo(map);

      marker.on('drag', () => {
        const { lng, lat } = marker.getLngLat();
        handlers.current.onWaypointDrag(waypoint.id, lng, lat, false);
      });
      marker.on('dragend', () => {
        const { lng, lat } = marker.getLngLat();
        // Only the end of a drag becomes an undo step.
        handlers.current.onWaypointDrag(waypoint.id, lng, lat, true);
      });

      markersRef.current.set(waypoint.id, marker);
    });

    for (const [id, marker] of markersRef.current) {
      if (!seen.has(id)) {
        marker.remove();
        markersRef.current.delete(id);
      }
    }
  }, [props.waypoints, props.selectedWaypointId]);

  // Fit to route on request
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !props.route || props.fitRequest === 0) return;
    const [west, south, east, north] = props.route.bbox;
    map.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: { top: 80, bottom: 80, left: 80, right: 80 }, duration: 600 },
    );
  }, [props.fitRequest]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="map-placeholder">
        <div>
          <h2>Mapbox token needed</h2>
          <p>
            Create <code>apps/web/.env.local</code> containing:
          </p>
          <pre>VITE_MAPBOX_TOKEN=pk.your_token_here</pre>
        </div>
      </div>
    );
  }

  return <div ref={containerRef} className="map-canvas" />;
}

/**
 * Dragging the route line to reshape it.
 *
 * Grabbing the line halfway round and pulling it onto a different road inserts a
 * shaping point *between the waypoints that bracket that spot* — never at the
 * end. Getting that index wrong is what makes a planner send you out to the new
 * point and back again.
 *
 * Re-routing happens on release rather than during the drag: a request per
 * mousemove would be both unusable and a good way to burn through an API quota.
 */
function wireLineDragging(
  map: mapboxgl.Map,
  handlers: React.MutableRefObject<MapCanvasProps>,
  dragRef: React.MutableRefObject<DragState | null>,
): void {
  const dragSource = () => map.getSource(DRAG_SOURCE) as mapboxgl.GeoJSONSource | undefined;

  map.on('mouseenter', ROUTE_HIT_LAYER, () => {
    if (!dragRef.current) map.getCanvas().style.cursor = 'grab';
  });
  map.on('mouseleave', ROUTE_HIT_LAYER, () => {
    if (!dragRef.current) map.getCanvas().style.cursor = 'crosshair';
  });

  map.on('mousedown', ROUTE_HIT_LAYER, (event) => {
    const { route, waypoints } = handlers.current;
    if (!route || waypoints.length < 2) return;

    // Stop the map panning under the gesture.
    event.preventDefault();
    map.dragPan.disable();
    map.getCanvas().style.cursor = 'grabbing';

    const grabbed: [number, number] = [event.lngLat.lng, event.lngLat.lat];
    const locations = locateWaypointsAlongRoute(route.coordinates, waypoints);
    const grabbedAlong = distanceAlongRoute(route.coordinates, grabbed)?.alongMeters ?? 0;
    const index = Math.max(1, Math.min(insertionIndexFor(locations, grabbedAlong), waypoints.length));

    const before = waypoints[index - 1];
    const after = waypoints[index];
    dragRef.current = {
      index,
      from: before ? [before.lng, before.lat] : null,
      to: after ? [after.lng, after.lat] : null,
      origin: event.point,
      moved: false,
    };

    paintDragPreview(dragSource(), dragRef.current, grabbed);

    const onMove = (moveEvent: mapboxgl.MapMouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      // A few pixels of travel separates a deliberate drag from a shaky click.
      if (Math.hypot(moveEvent.point.x - drag.origin.x, moveEvent.point.y - drag.origin.y) > DRAG_THRESHOLD_PX) {
        drag.moved = true;
      }
      paintDragPreview(dragSource(), drag, [moveEvent.lngLat.lng, moveEvent.lngLat.lat]);
    };

    const onUp = (upEvent: mapboxgl.MapMouseEvent) => {
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      map.dragPan.enable();
      map.getCanvas().style.cursor = 'crosshair';
      dragSource()?.setData(EMPTY_COLLECTION);

      const pending = dragRef.current;
      // Cleared on the next tick so the map's own `click`, which fires after
      // mouseup, knows to ignore this gesture rather than appending a waypoint.
      setTimeout(() => {
        dragRef.current = null;
      }, 0);

      // Clicking the line without moving shouldn't drop a point that changes
      // nothing — but it still shouldn't fall through and append one either.
      if (!pending || !pending.moved) return;
      handlers.current.onInsertWaypoint(pending.index, upEvent.lngLat.lng, upEvent.lngLat.lat);
    };

    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
  });
}

function paintDragPreview(
  source: mapboxgl.GeoJSONSource | undefined,
  drag: { from: [number, number] | null; to: [number, number] | null },
  cursor: [number, number],
): void {
  if (!source) return;
  const line: Array<[number, number]> = [];
  if (drag.from) line.push(drag.from);
  line.push(cursor);
  if (drag.to) line.push(drag.to);

  source.setData({
    type: 'FeatureCollection',
    features: [
      ...(line.length > 1
        ? [
            {
              type: 'Feature' as const,
              properties: {},
              geometry: { type: 'LineString' as const, coordinates: line },
            },
          ]
        : []),
      {
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'Point' as const, coordinates: cursor },
      },
    ],
  });
}

function syncRoute(map: mapboxgl.Map, route: DirectionsResult | null): void {
  const source = map.getSource(ROUTE_SOURCE) as mapboxgl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData(
    route
      ? {
          type: 'Feature',
          properties: {},
          // Smoothed for display only; every calculation uses route.coordinates,
          // so dragging and insertion still work against the real geometry.
          geometry: { type: 'LineString', coordinates: smoothPath(route.coordinates) },
        }
      : EMPTY_COLLECTION,
  );
}

function labelFor(waypoint: Waypoint, index: number, total: number): string {
  if (index === 0) return 'A';
  if (index === total - 1) return 'B';
  return waypoint.kind === 'stop' ? String(index) : '';
}

function paintMarker(element: HTMLElement, waypoint: Waypoint, label: string, selected: boolean): void {
  element.textContent = label;
  element.dataset.kind = waypoint.kind;
  element.dataset.selected = String(selected);
  element.title = waypoint.name ?? (waypoint.kind === 'stop' ? 'Stop' : 'Shaping point');
}
